import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import { ConfirmDialog, PageHeader, TableActions } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { AgingSchema } from '../utils/agingFormSchema';
import {
  getFreeAgingVessels,
  getModalAgingVessels,
  normalizeAgingVesselId,
  resolveAgingVesselId,
} from '../utils/agingVesselAvailability';
import {
  AGING_COMPLETED_CONFIRM_QUESTION,
  AGING_COMPLETED_CONFIRM_TITLE,
  AGING_MUST_COMPLETE_FOR_BOTTLING,
  completeAgingForBottling,
} from '../utils/completeAgingForBottling';
import { ExcelColumn, exportToExcel } from '../utils/exportToExcel';

export interface AgingProcess {
  id: number;
  batch_id: number;
  vesselId: number;
  /** API may return snake_case FK when underscored model is serialized. */
  vessel_id?: number;
  start_date: string;
  end_date?: string;
  status: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  batchNumber?: string;
  previousFermentationId?: number;
  batchId?: number;
  startDate?: string;
  endDate?: string;
  /** Linked bottling runs for this aging process (client-enriched). */
  bottlingCount?: number;
  batch?: {
    id: number;
    batch_id: string;
    batch_name: string;
    vineyard?: {
      id: number;
      name: string;
    }
  };
  vessel?: {
    id: number;
    vesselId: string;
    vessel_id: string;
    type: string;
  };
  previousFermentation?: {
    id: number;
    start_date: string;
    end_date?: string;
    status: string;
  };
}

interface AgingFormValues {
  batch_id: number;
  vesselId: number;
  start_date: string;
  end_date?: string;
  status: string;
  notes?: string;
}

// Helper function for safer date formatting
const formatDate = (dateString: string | undefined | null): string => {
  console.log('formatDate input:', dateString, 'type:', typeof dateString);

  if (!dateString) {
    console.log('formatDate: dateString is falsy, returning N/A');
    return 'N/A';
  }

  try {
    // First, try to clean the date string if needed
    let cleanDateStr = dateString;

    // Handle timezone issues by ensuring we're working with a clean YYYY-MM-DD format
    if (dateString.includes('T')) {
      cleanDateStr = dateString.split('T')[0];
    }

    console.log('formatDate: cleanDateStr:', cleanDateStr);

    // Try to create a valid date object
    const date = new Date(cleanDateStr);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error("Invalid date detected:", dateString);
      return 'Invalid Date';
    }

    // Use a more standard, unambiguous format
    const formatted = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    console.log('formatDate: formatted result:', formatted);
    return formatted;
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
    return 'Invalid Date';
  }
};

// Helper function to safely format dates for form inputs (YYYY-MM-DD)
const formatDateForInput = (dateString: string | undefined | null): string => {
  console.log('formatDateForInput called with:', dateString, 'type:', typeof dateString);

  if (!dateString) {
    console.log('formatDateForInput: dateString is falsy, returning empty string');
    return '';
  }

  try {
    // Handle timezone issues by ensuring we're working with a clean date
    let cleanDateStr = dateString;

    // If it's an ISO string, extract just the date part
    if (dateString.includes('T')) {
      cleanDateStr = dateString.split('T')[0];
      console.log('formatDateForInput: extracted date part from ISO string:', cleanDateStr);
    }

    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleanDateStr)) {
      console.log('formatDateForInput: already in correct format, returning:', cleanDateStr);
      return cleanDateStr;
    }

    // Try to parse and format
    const date = new Date(cleanDateStr);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error("formatDateForInput: Invalid date for input:", dateString);
      return '';
    }

    // Return in YYYY-MM-DD format for input[type="date"]
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    const result = `${year}-${month}-${day}`;
    console.log('formatDateForInput: formatted result:', result);
    return result;
  } catch (error) {
    console.error("formatDateForInput: Error formatting date for input:", dateString, error);
    return '';
  }
};

// Helper function to format batch display
const formatBatchDisplay = (batch: any): { title: string; subtitle: string } => {
  if (!batch) {
    return { title: 'No ID', subtitle: 'Unnamed Batch' };
  }

  const batchId = batch.batchId || batch.batch_id || 'No ID';
  const batchName = batch.batchName || batch.batch_name || 'Unnamed Batch';
  const vineyardName = batch.vineyard?.name || (batch.vineyard && batch.vineyard.name);

  if (vineyardName) {
    return {
      title: `${vineyardName} - ${batchId}`,
      subtitle: batchName
    };
  }

  return {
    title: batchId,
    subtitle: batchName
  };
};

const Aging: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const { user } = useAuth();
  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';
  const [agingProcesses, setAgingProcesses] = useState<AgingProcess[]>([]);
  const [wineBatches, setWineBatches] = useState<any[]>([]);
  const [vessels, setVessels] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  /** API errors from create/update — shown inside the aging modal */
  const [formError, setFormError] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingAging, setEditingAging] = useState<AgingProcess | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [bottlingConfirmAging, setBottlingConfirmAging] = useState<AgingProcess | null>(null);
  const [bottlingConfirmError, setBottlingConfirmError] = useState<string | null>(null);
  const [bottlingConfirmLoading, setBottlingConfirmLoading] = useState(false);
  const navigate = useNavigate();

  // Debug when editingAging changes
  useEffect(() => {
    console.log('editingAging changed:', editingAging);
    if (editingAging) {
      console.log('editingAging start_date:', editingAging.start_date);
      console.log('editingAging end_date:', editingAging.end_date);
    }
  }, [editingAging]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [agingRes, batchesRes, vesselsRes, bottlingRes] = await Promise.all([
          api.get('/api/aging'),
          api.get('/api/batch'),
          api.get('/api/vessel'),
          api.get('/api/bottling').catch(() => ({ data: [] }))
        ]);

        console.log('Raw aging data:', agingRes.data);
        console.log('Raw batches data:', batchesRes.data);
        console.log('Raw vessels data:', vesselsRes.data);

        const bottlingCounts = new Map<number, number>();
        const bottlingRows = Array.isArray(bottlingRes.data) ? bottlingRes.data : [];
        for (const run of bottlingRows) {
          const agingId = Number(run.agingId ?? run.aging_id ?? run.aging?.id ?? 0);
          if (!agingId) continue;
          bottlingCounts.set(agingId, (bottlingCounts.get(agingId) ?? 0) + 1);
        }

        // Process the aging data to ensure batch properties are accessible
        const processedAgingData = agingRes.data.map((aging: any) => {
          // Add validation and debugging
          const hasBatch = !!aging.batch;
          const hasBatchId = hasBatch && 'batch_id' in aging.batch;
          const hasBatchName = hasBatch && 'batch_name' in aging.batch;
          const hasVineyard = hasBatch && !!aging.batch.vineyard;

          // Debug the date fields
          console.log(`Processing aging ${aging.id} dates:`, {
            start_date: aging.start_date,
            startDate: aging.startDate,
            end_date: aging.end_date,
            endDate: aging.endDate,
            createdAt: aging.createdAt,
            updatedAt: aging.updatedAt
          });

          // Ensure start_date is available (handle both camelCase and snake_case)
          if (!aging.start_date && aging.startDate) {
            aging.start_date = aging.startDate;
          }

          // Ensure end_date is available (handle both camelCase and snake_case)
          if (!aging.end_date && aging.endDate) {
            aging.end_date = aging.endDate;
          }

          console.log(`Processing aging ${aging.id}:`, {
            hasBatch,
            hasBatchId: hasBatchId ? aging.batch.batch_id : false,
            hasBatchName: hasBatchName ? aging.batch.batch_name : false,
            hasVineyard: hasVineyard ? (aging.batch.vineyard.name || true) : false,
            final_start_date: aging.start_date,
            final_end_date: aging.end_date
          });

          // Debug the final aging object structure
          console.log(`Final aging object ${aging.id}:`, {
            id: aging.id,
            start_date: aging.start_date,
            end_date: aging.end_date,
            status: aging.status
          });

          // Return the aging object with validated batch data, normalized vessel FK, and bottling count
          return {
            ...normalizeAgingVesselId(aging),
            bottlingCount: bottlingCounts.get(Number(aging.id)) ?? 0
          };
        });

        setAgingProcesses(processedAgingData);
        setWineBatches(batchesRes.data);
        setVessels(vesselsRes.data);
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (tabVisible) fetchData();
  }, [tabVisible]);

  // Free vessels for starting new aging (page banner + Start New Aging gate)
  const freeAgingVessels = useMemo(
    () => getFreeAgingVessels(vessels, agingProcesses),
    [agingProcesses, vessels]
  );

  // Selectable vessels in the modal (includes current vessel when editing, even if In Use)
  const modalVessels = useMemo(
    () => getModalAgingVessels(vessels, agingProcesses, editingAging),
    [agingProcesses, vessels, editingAging]
  );

  const openModal = (aging: AgingProcess | null = null) => {
    console.log('openModal called with aging:', aging);
    if (aging) {
      console.log('aging.start_date:', aging.start_date);
      console.log('aging.end_date:', aging.end_date);
    }
    setFormError(null);
    setEditingAging(aging);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingAging(null);
    setFormError(null);
  };

  const navigateToBottling = (agingId: number) => {
    navigate(`/bottling?agingId=${agingId}`);
  };

  const handleCreateBottlingClick = (aging: AgingProcess) => {
    if (aging.status === 'Completed') {
      navigateToBottling(aging.id);
      return;
    }
    setBottlingConfirmError(null);
    setBottlingConfirmAging(aging);
  };

  const closeBottlingConfirm = () => {
    setBottlingConfirmAging(null);
    setBottlingConfirmError(null);
    setBottlingConfirmLoading(false);
  };

  const handleConfirmAgingCompleted = async () => {
    if (!bottlingConfirmAging) return;
    setBottlingConfirmLoading(true);
    setBottlingConfirmError(null);
    try {
      await completeAgingForBottling(bottlingConfirmAging);
      const agingId = bottlingConfirmAging.id;
      const endDate =
        bottlingConfirmAging.end_date ||
        bottlingConfirmAging.endDate ||
        new Date().toISOString().split('T')[0];
      setAgingProcesses((prev) =>
        prev.map((a) =>
          a.id === agingId ? { ...a, status: 'Completed', end_date: endDate } : a
        )
      );
      closeBottlingConfirm();
      navigateToBottling(agingId);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to complete aging process.';
      setBottlingConfirmError(message);
      setBottlingConfirmLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to delete this aging record?')) return;
    try {
      await api.delete(`/api/aging/${id}`);
      setAgingProcesses(prev => prev.filter(a => a.id !== id));
      setSuccessMessage('Aging record deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      const data = err.response?.data;
      if (err.response?.status === 409 && data?.dependencies) {
        const d = data.dependencies || {};
        const parts: string[] = [];
        if (d.agingMaintenance) parts.push(`${d.agingMaintenance} maintenance`);
        if (d.bottlings) parts.push(`${d.bottlings} bottlings`);
        setError(`Cannot delete aging record. Delete related ${parts.join(', ')} first.`);
      } else {
        setError(err);
      }
    }
  };

  const handleSubmit = async (values: AgingFormValues, { resetForm, setSubmitting }: any) => {
    try {
      setFormError(null);
      let updatedAging: AgingProcess;

      // Map form values to the format expected by the API
      const apiValues: Record<string, any> = {
        batch_id: values.batch_id,
        vessel_id: values.vesselId,
        start_date: values.start_date,
        status: values.status,
        notes: values.notes
      };
      if (values.end_date) {
        apiValues.end_date = values.end_date;
      }

      if (editingAging) {
        // Update existing aging process
        const updateResponse = await api.put(`/api/aging/${editingAging.id}`, apiValues);
        console.log("Update response:", updateResponse.data);

        // Fetch the updated aging record to ensure we have complete data
        const refreshedAging = await api.get(`/api/aging/${editingAging.id}`);
        console.log("Refreshed aging data:", refreshedAging.data);

        // Use the refreshed data which should have proper associations
        updatedAging = normalizeAgingVesselId({
          ...refreshedAging.data,
          // Ensure critical fields are set properly
          id: editingAging.id,
          batch_id: refreshedAging.data.batchId || refreshedAging.data.batch_id || values.batch_id,
          vesselId: refreshedAging.data.vesselId || refreshedAging.data.vessel_id || values.vesselId,
          vessel_id: refreshedAging.data.vessel_id || refreshedAging.data.vesselId || values.vesselId,
          start_date: refreshedAging.data.start_date || refreshedAging.data.startDate || values.start_date,
          end_date: refreshedAging.data.end_date || refreshedAging.data.endDate || values.end_date,
          status: refreshedAging.data.status || values.status,
          notes: refreshedAging.data.notes || values.notes,
          createdAt: refreshedAging.data.createdAt || editingAging.createdAt,
          updatedAt: refreshedAging.data.updatedAt || new Date().toISOString()
        });

        // Update state with the refreshed data
        setAgingProcesses(agingProcesses.map(a => (a.id === editingAging.id ? updatedAging : a)));
        setSuccessMessage('Aging process updated successfully!');
      } else {
        // Create new aging process
        const createResponse = await api.post('/api/aging', apiValues);
        console.log("Create response:", createResponse.data);

        // Transform response data to ensure all fields are properly formatted
        updatedAging = normalizeAgingVesselId({
          ...createResponse.data,
          // Handle camelCase to snake_case conversions
          id: createResponse.data.id,
          batch_id: createResponse.data.batchId || createResponse.data.batch_id,
          vesselId: createResponse.data.vesselId ?? createResponse.data.vessel_id,
          vessel_id: createResponse.data.vessel_id ?? createResponse.data.vesselId,
          start_date: createResponse.data.startDate || createResponse.data.start_date,
          end_date: createResponse.data.endDate || createResponse.data.end_date,
          status: createResponse.data.status,
          notes: createResponse.data.notes,
          createdAt: createResponse.data.createdAt || new Date().toISOString(),
          updatedAt: createResponse.data.updatedAt || new Date().toISOString(),
          batchNumber: createResponse.data.batchNumber,
          previousFermentationId: createResponse.data.previousFermentationId,
          batch: createResponse.data.batch,
          vessel: createResponse.data.vessel,
          previousFermentation: createResponse.data.previousFermentation
        });

        // Update state with properly transformed data
        setAgingProcesses([updatedAging, ...agingProcesses]);
        setSuccessMessage('Aging process started successfully!');
      }

      resetForm();
      closeModal();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error saving aging process:', err);
      setFormError(err);
    } finally {
      setSubmitting(false);
    }
  };

  // Export aging processes to Excel (desktop dataset).
  const handleExportToExcel = () => {
    if (!agingProcesses.length) return;

    const columns: ExcelColumn<AgingProcess>[] = [
      {
        header: 'Wine Batch',
        accessor: (a) => {
          const batch = a.batch;
          if (!batch) return 'Unknown';
          const vineyardName = batch.vineyard?.name;
          const batchId = batch.batch_id;
          const batchName = batch.batch_name;
          return vineyardName
            ? `${vineyardName} - ${batchId}`
            : batchId || batchName || 'Unknown';
        },
      },
      {
        header: 'Vessel',
        accessor: (a) =>
          a.vessel
            ? a.vessel.vesselId || a.vessel.vessel_id || `#${a.vessel.id}`
            : '',
      },
      {
        header: 'Start Date',
        accessor: (a) => a.start_date || '',
      },
      {
        header: 'End Date',
        accessor: (a) => a.end_date || '',
      },
      {
        header: 'Status',
        accessor: (a) => a.status,
      },
    ];

    exportToExcel({
      fileName: `aging-${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: 'Aging',
      rows: agingProcesses,
      columns,
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-success-500 text-white';
      case 'Completed':
        return 'bg-info-500 text-white';
      case 'On Hold':
        return 'bg-warning-500 text-white';
      case 'Cancelled':
        return 'bg-error-500 text-white';
      default:
        return 'bg-monday-status-neutral text-white';
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Aging Management"
        subtitle="Track and manage wine aging processes in barrels and tanks."
        variant="compact"
        actions={(
          <div className="flex space-x-3">
            <div className="relative flex items-center gap-3">
              <button
                type="button"
                onClick={handleExportToExcel}
                className="inline-flex items-center px-4 py-2 border border-monday-border text-sm font-medium rounded-md shadow-sm text-monday-text bg-white hover:bg-monday-hover"
              >
                Export to Excel
              </button>
              {!freeAgingVessels.length && (
                <div className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm font-medium text-red-700 shadow-sm" role="alert" aria-live="polite">
                  <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.518 11.591c.75 1.335-.213 2.985-1.743 2.985H3.482c-1.53 0-2.493-1.65-1.743-2.985L8.257 3.1zM11 14a1 1 0 10-2 0 1 1 0 002 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V7a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <span>No available aging vessels</span>
                  <Link to="/vessels" className="font-semibold underline hover:text-red-900">
                    Manage
                  </Link>
                </div>
              )}
              <PermissionButton
                resource="aging"
                action="create"
                onClick={() => freeAgingVessels.length ? openModal() : null}
                className={!freeAgingVessels.length ? '!bg-neutral-400 !cursor-not-allowed' : ''}
              >
                Start New Aging
              </PermissionButton>
            </div>
          </div>
        )}
      />

      {successMessage && (
        <div className="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {error && (
        <ErrorMessageComponent error={error} className="mt-4" />
      )}

      <div className="mt-8 flex flex-col">
        {/* Desktop Table View */}
        <div className="hidden sm:block">
          <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                <table className="min-w-full divide-y divide-monday-border">
                  <thead className="bg-monday-header">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Wine Batch</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Vessel</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Start Date</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">End Date</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Status</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-monday-text-muted uppercase tracking-wider">Bottling</th>
                      <th scope="col" className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-monday-border-light bg-white">
                    {agingProcesses.length > 0 ? (
                      agingProcesses.map((aging) => {
                        const batchDisplay = formatBatchDisplay(aging.batch);
                        const bottlingCount = aging.bottlingCount ?? 0;
                        return (
                          <tr key={aging.id} className="group hover:bg-monday-hover transition-colors duration-150">
                            <td className="whitespace-nowrap px-6 py-4">
                              <div className="flex flex-col">
                                <span className="data-row-primary">{batchDisplay.title}</span>
                                <span className="data-row-secondary">{batchDisplay.subtitle}</span>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 data-cell-muted cursor-pointer hover:text-primary-600" onClick={() => { if (aging.vessel) { navigate(`/vessel/${aging.vessel.id}`); } }}>
                              {aging.vessel ? (aging.vessel.vesselId || `#${aging.vessel.id}`) : '-'}
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-monday-text">{formatDate(aging.start_date)}</td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-monday-text">{formatDate(aging.end_date)}</td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-monday-text">
                              <span className={`inline-flex rounded px-2 text-xs font-semibold leading-5 ${getStatusClass(aging.status)}`}>{aging.status}</span>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-sm text-monday-text">
                              <span
                                data-testid={`aging-bottling-count-${aging.id}`}
                                className="text-monday-text-muted"
                              >
                                {bottlingCount === 0
                                  ? 'None'
                                  : `${bottlingCount} run${bottlingCount === 1 ? '' : 's'}`}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                              <TableActions>
                                <PermissionButton
                                  resource="bottling"
                                  action="create"
                                  onClick={() => handleCreateBottlingClick(aging)}
                                  variant="secondary"
                                  className="!px-2.5 !py-1 !text-sm !bg-transparent !text-primary-600 hover:!bg-primary-50 hover:!text-primary-700 !border-0 !shadow-none"
                                >
                                  Create bottling
                                </PermissionButton>
                                <PermissionButton
                                  resource="aging"
                                  action="update"
                                  onClick={() => openModal(aging)}
                                  variant="secondary"
                                  className="!px-2.5 !py-1 !text-sm !bg-transparent !text-monday-text-muted hover:!bg-monday-hover hover:!text-monday-text !border-0 !shadow-none"
                                >
                                  Edit
                                </PermissionButton>
                                {isAdmin && (
                                  <button
                                    onClick={() => handleDelete(aging.id)}
                                    className="px-2.5 py-1 text-sm font-medium text-monday-text-muted hover:text-error-600 hover:bg-error-50 rounded-md transition-colors"
                                  >
                                    Delete
                                  </button>
                                )}
                              </TableActions>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} className="py-4 text-center text-sm text-monday-text-muted">
                          No aging processes found. Start your first aging process!
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile Card/List View */}
        <div className="sm:hidden space-y-4">
          {agingProcesses.length > 0 ? (
            agingProcesses.map((aging) => {
              const batchDisplay = formatBatchDisplay(aging.batch);
              return (
                <div key={aging.id} className="bg-white shadow rounded-lg p-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="data-row-primary">{batchDisplay.title}</div>
                      <div className="data-row-secondary">{batchDisplay.subtitle}</div>
                    </div>
                    <span className={`inline-flex rounded px-2 text-xs font-semibold leading-5 ${getStatusClass(aging.status)}`}>{aging.status}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 text-sm text-monday-text">
                    <div><span className="font-medium">Vessel:</span> {aging.vessel ? (aging.vessel.vesselId || `#${aging.vessel.id}`) : '-'}</div>
                    <div><span className="font-medium">Start:</span> {formatDate(aging.start_date)}</div>
                    <div><span className="font-medium">End:</span> {formatDate(aging.end_date)}</div>
                    <div data-testid={`aging-bottling-count-${aging.id}`}>
                      <span className="font-medium">Bottling:</span>{' '}
                      {(aging.bottlingCount ?? 0) === 0
                        ? 'None'
                        : `${aging.bottlingCount} run${aging.bottlingCount === 1 ? '' : 's'}`}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-2">
                    <PermissionButton
                      resource="bottling"
                      action="create"
                      onClick={() => handleCreateBottlingClick(aging)}
                      variant="secondary"
                      className="!flex-1 !py-2 !text-xs"
                    >
                      Create bottling
                    </PermissionButton>
                    <PermissionButton
                      resource="aging"
                      action="update"
                      onClick={() => openModal(aging)}
                      variant="secondary"
                      className="!flex-1 !py-2 !text-xs"
                    >
                      Edit
                    </PermissionButton>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center text-monday-text-muted py-8">No aging processes found. Start your first aging process!</div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={!!bottlingConfirmAging}
        onClose={closeBottlingConfirm}
        onConfirm={() => { void handleConfirmAgingCompleted(); }}
        onDecline={() => setBottlingConfirmError(AGING_MUST_COMPLETE_FOR_BOTTLING)}
        title={AGING_COMPLETED_CONFIRM_TITLE}
        message={AGING_COMPLETED_CONFIRM_QUESTION}
        confirmText="Yes"
        cancelText="No"
        variant="warning"
        isLoading={bottlingConfirmLoading}
        errorMessage={bottlingConfirmError || undefined}
      />

      <MobileModal isOpen={isModalOpen} onClose={closeModal} title={editingAging ? 'Edit Aging Process' : 'Start New Aging Process'} size="full">
        <div className="mobile-form-container">
          <Formik
            initialValues={{
              batch_id: editingAging?.batch_id ?? editingAging?.batchId ?? (wineBatches.length > 0 ? wineBatches[0].id : 0),
              vesselId: resolveAgingVesselId(editingAging) ?? (modalVessels.length > 0 ? modalVessels[0].id : 0),
              start_date: (() => {
                const startDate = formatDateForInput(editingAging?.start_date ?? editingAging?.startDate) || new Date().toISOString().split('T')[0];
                console.log('Formik initialValues start_date:', startDate);
                return startDate;
              })(),
              end_date: (() => {
                const endDate = formatDateForInput(editingAging?.end_date ?? editingAging?.endDate);
                console.log('Formik initialValues end_date:', endDate);
                return endDate;
              })(),
              status: editingAging?.status ?? 'Active',
              notes: editingAging?.notes ?? ''
            }}
            validationSchema={AgingSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, values }) => {
              const isEditing = Boolean(editingAging);
              const canSubmitVessel = isEditing
                ? modalVessels.length > 0
                : freeAgingVessels.length > 0;
              const submitLabel = isSubmitting
                ? 'Saving...'
                : isEditing
                  ? 'Update'
                  : !freeAgingVessels.length
                    ? 'No Available Vessels'
                    : 'Start Aging';

              return (
              <Form className="space-y-4">
                {formError && (
                  <ErrorMessageComponent error={formError} className="mb-2" />
                )}

                <div>
                  <label htmlFor="batch_id" className="form-label">
                    Wine Batch
                  </label>
                  <Field as="select" name="batch_id" id="batch_id" className="mobile-form-input">
                    {wineBatches.map((batch) => (
                      <option key={batch.id} value={batch.id}>
                        {batch.vineyard?.name || 'Unknown Vineyard'} - {batch.batch_id || (batch as any).batchId || `#${batch.id}`} - {batch.batch_name || (batch as any).batchName || 'Unnamed Batch'} ({batch.grape_variety || (batch as any).grapeVariety || batch.wine_type || (batch as any).wineType || 'Unknown Type'})
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage name="batch_id" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div>
                  <label htmlFor="vesselId" className="form-label">
                    Vessel
                  </label>
                  <Field as="select" name="vesselId" id="vesselId" className="mobile-form-input" disabled={!modalVessels.length}>
                    <option value="">{modalVessels.length ? 'Select a vessel' : 'No available vessels'}</option>
                    {modalVessels.map((vessel) => (
                      <option key={vessel.id} value={vessel.id}>
                        {vessel.vesselId || vessel.vessel_id || `#${vessel.id}`} - {vessel.type} ({vessel.capacity} {vessel.capacity_unit || 'L'}) - {vessel.material || 'Unknown Material'}
                      </option>
                    ))}
                  </Field>
                  {isEditing && freeAgingVessels.length === 0 && modalVessels.length > 0 && (
                    <p className="mt-1 text-xs text-monday-text-muted">
                      Keeping current vessel. Free vessels are only required to start new aging.
                    </p>
                  )}
                  <ErrorMessage name="vesselId" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div>
                  <label htmlFor="start_date" className="form-label">
                    Start Date
                  </label>
                  <Field type="date" name="start_date" id="start_date" className="mobile-form-input" />
                  <ErrorMessage name="start_date" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div>
                  <label htmlFor="end_date" className="form-label">
                    End Date{values.status === 'Completed' ? '' : ' (Optional)'}
                  </label>
                  <Field type="date" name="end_date" id="end_date" className="mobile-form-input" />
                  <ErrorMessage name="end_date" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div>
                  <label htmlFor="status" className="form-label">
                    Status
                  </label>
                  <Field as="select" name="status" id="status" className="mobile-form-input">
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Cancelled">Cancelled</option>
                  </Field>
                  <ErrorMessage name="status" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div>
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <Field as="textarea" name="notes" id="notes" rows={3} className="mobile-form-input resize-none" placeholder="Additional notes about the aging process..." />
                  <ErrorMessage name="notes" component="div" className="mt-1 text-sm text-red-600" />
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                  <button type="submit" disabled={isSubmitting || !canSubmitVessel} className={`mobile-btn mobile-btn-primary w-full sm:w-auto ${!canSubmitVessel ? 'opacity-60 cursor-not-allowed' : ''}`}>
                    {submitLabel}
                  </button>
                  <button type="button" onClick={closeModal} className="mobile-btn mobile-btn-secondary w-full sm:w-auto">
                    Cancel
                  </button>
                </div>
              </Form>
              );
            }}
          </Formik>
        </div>
      </MobileModal>
    </div>
  );
};

export default Aging; 